import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "./errors";

/**
 * App-wide QueryClient defaults:
 * - 4xx responses are the caller's problem, never retried; everything else
 *   (network, 5xx) gets up to 2 retries.
 * - staleTime 30s — ERP list/detail data tolerates short staleness.
 * - refetchOnWindowFocus stays at the default `true` (refetch stale queries
 *   on focus); "always" would refire fresh queries on every tab switch and
 *   hammer list endpoints.
 * - Mutations are never auto-retried; POST Idempotency-Keys make explicit
 *   user-driven retries safe instead.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Filters embedded in a list key; must be JSON-serializable and stable. */
export type KeyFilters = Record<string, unknown>;

/**
 * Generic query-key factory. One scope per API resource; every key is a
 * readonly tuple rooted at that scope so invalidation can target
 * `keyFactory(scope).all`.
 */
export function keyFactory<Scope extends string>(scope: Scope) {
  return {
    all: [scope] as const,
    current: () => [scope, "current"] as const,
    list: (filters?: KeyFilters) => [scope, "list", filters ?? null] as const,
    detail: (id: string) => [scope, "detail", id] as const,
  };
}

/**
 * Central key registry — add a scope per resource as features land, e.g.
 * queryKeys.session.current(), queryKeys.auditLog.list({ actor: "…" }).
 */
export const queryKeys = {
  session: keyFactory("session"),
  auditLog: keyFactory("audit-log"),
} as const;
